import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { pharmacyApi } from "@/api/pharmacy";

export default function PharmacyReturnHistoryPage() {
  const todayIso = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(todayIso);
  const [to, setTo] = useState(todayIso);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["pharmacy-return-history", from, to],
    queryFn: async () => {
      const res = await pharmacyApi.getReturnHistory({ from, to });
      return res.data?.data as { transactions?: any[] };
    },
  });

  const txns = data?.transactions ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Return History</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">From date</label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">To date</label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
            </div>
            <Button type="button" onClick={() => refetch()} disabled={isLoading}>
              {isLoading ? "Loading..." : "Apply"}
            </Button>
          </div>

          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Bill no.</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Qty returned</TableHead>
                  <TableHead className="text-right">Refund (₹)</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-6 text-gray-500">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : txns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-6 text-gray-500">
                      No returns found for this range.
                    </TableCell>
                  </TableRow>
                ) : (
                  txns.map((t: any) => {
                    const lines = Array.isArray(t.returns) ? t.returns : [];
                    const totalQty = lines.reduce((s: number, r: any) => s + (r.quantityReturned ?? 0), 0);
                    const itemsPreview = lines
                      .slice(0, 2)
                      .map((r: any) => r.pharmacyIssue?.drug?.name ?? "—")
                      .join(", ");
                    return (
                    <TableRow key={t.id}>
                      <TableCell className="text-xs">
                        {t.createdAt ? new Date(t.createdAt).toLocaleString("en-IN") : "-"}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {t.bill?.billNumber ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {t.bill?.patient?.name ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {itemsPreview || "—"}
                      </TableCell>
                      <TableCell className="text-right">{totalQty}</TableCell>
                      <TableCell className="text-right">{Number(t.refundAmount ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="text-xs">{t.refundMethod?.toString().replace(/_/g, " ") ?? "—"}</TableCell>
                      <TableCell className="text-xs">{t.performedBy?.name ?? "—"}</TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

